import Papa from 'papaparse';

import { SlidingScalePricingItem, TourService } from '~/types/tours/costPrice';

export const CSV_COLUMNS = {
  ACCOUNT_ID: 'AccountId(DMC)',
  ACCOUNT_NAME: 'Account Name',
  OPPORTUNITY_ID: 'OpportunityId(Tour)',
  SEASON: 'Season',
  PACKAGE: 'Package',
  PACKAGE_OPTION: 'PackageOption',
  SINGLE_SUPPLEMENT: 'Single Supplement',
  CURRENCY: 'currencyisocode',
} as const;

/** The parts of a service the template needs. */
export type TemplateService = Pick<TourService, 'name' | 'configurations'>;

/** Suppliers quote sliding scales up to this many pax. */
export const MAX_PAX_COLUMN = 24;

const PAX_COLUMNS = Array.from({ length: MAX_PAX_COLUMN }, (_, index) => String(index + 1));

export const REQUIRED_COLUMNS: Array<string> = [
  CSV_COLUMNS.ACCOUNT_ID,
  CSV_COLUMNS.ACCOUNT_NAME,
  CSV_COLUMNS.OPPORTUNITY_ID,
  CSV_COLUMNS.SEASON,
  CSV_COLUMNS.PACKAGE,
  CSV_COLUMNS.PACKAGE_OPTION,
  CSV_COLUMNS.SINGLE_SUPPLEMENT,
  CSV_COLUMNS.CURRENCY,
];

export const TEMPLATE_COLUMNS: Array<string> = [...REQUIRED_COLUMNS, ...PAX_COLUMNS];

/** A parsed row, ready to send as one item of the upload payload. */
export interface ParsedSlidingScaleRow {
  item: SlidingScalePricingItem;
  /** Line in the source file, for reporting problems back to the uploader. */
  rowNumber: number;
}

export interface SlidingScaleCsvParseResult {
  rows: Array<ParsedSlidingScaleRow>;
  errors: Array<string>;
}

/**
 * Excel writes a UTF-8 BOM ahead of the first header, which would otherwise
 * become part of the first column name.
 */
function stripBom(value: string): string {
  return value.replace(/^\uFEFF/, '');
}

function normaliseHeader(header: string): string {
  return stripBom(header).trim();
}

/**
 * Supplier sheets frequently carry thousands separators and currency symbols,
 * and blank cells where a pax count is not quoted.
 */
function parseAmount(raw: string | undefined): number | null {
  if (raw === undefined) {
    return null;
  }

  const cleaned = raw.replace(/[^0-9.-]/g, '').trim();
  if (cleaned === '') {
    return null;
  }

  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Reads the pax columns into svc-tour's positional scale array, where index 0
 * is the rate for 1 pax.
 *
 * Because the scale is positional it cannot express a gap, so a blank cell
 * between two quoted rates is reported rather than silently shifting every
 * later rate down a pax.
 */
function parseScale(raw: Record<string, string>, rowNumber: number): { scale: Array<number>; error?: string } {
  const amounts = PAX_COLUMNS.map((paxColumn) => parseAmount(raw[paxColumn]));

  const lastQuotedIndex = amounts.reduce((last, amount, index) => (amount === null ? last : index), -1);

  if (lastQuotedIndex === -1) {
    return { scale: [], error: `Row ${rowNumber}: no pax cost prices found in columns 1-${MAX_PAX_COLUMN}` };
  }

  const scale = amounts.slice(0, lastQuotedIndex + 1);
  const gapIndex = scale.findIndex((amount) => amount === null);

  if (gapIndex !== -1) {
    return {
      scale: [],
      error: `Row ${rowNumber}: pax ${gapIndex + 1} is blank but pax ${lastQuotedIndex + 1} has a rate; the scale must be filled in from 1 pax with no gaps`,
    };
  }

  return { scale: scale };
}

/**
 * Builds a template pre-filled from the service the upload will target.
 *
 * Package and PackageOption are the columns svc-tour matches on, so they are
 * filled in from the service's own configurations - one row per combination -
 * rather than left for someone to type and mistype.
 *
 * The Salesforce identity columns are left blank: the service carries an
 * internal supplierId, not the DMC account or opportunity ids, and a plausible
 * looking placeholder would be worse than an empty cell. Season, single
 * supplement and the pax rates are left blank for the supplier's figures.
 */
export function buildTemplateCsv(service?: TemplateService): string {
  const configurations = (service?.configurations ?? []).flatMap((configuration) =>
    configuration.packages.map((packageConfiguration) => ({
      [CSV_COLUMNS.PACKAGE]: configuration.name,
      [CSV_COLUMNS.PACKAGE_OPTION]: packageConfiguration.name,
    })),
  );

  // A service with no configurations still gets a usable blank row.
  const rows = configurations.length ? configurations : [{}];

  return Papa.unparse({
    fields: TEMPLATE_COLUMNS,
    data: rows.map((row) => TEMPLATE_COLUMNS.map((column) => row[column] ?? '')),
  });
}

/** Filename that ties a downloaded template back to the service it came from. */
export function buildTemplateFilename(service?: TemplateService): string {
  const slug = (service?.name ?? 'cost-price')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

  return `${slug || 'cost-price'}-sliding-scale-template.csv`;
}

export function parseSlidingScaleCsv(csv: string): SlidingScaleCsvParseResult {
  const parsed = Papa.parse<Record<string, string>>(csv, {
    header: true,
    skipEmptyLines: true,
    transformHeader: normaliseHeader,
  });

  const errors: Array<string> = parsed.errors.map((error) => `Row ${(error.row ?? 0) + 2}: ${error.message}`);

  const headers = (parsed.meta.fields ?? []).map(normaliseHeader);
  const missingColumns = REQUIRED_COLUMNS.filter((column) => !headers.includes(column));
  if (missingColumns.length) {
    return {
      rows: [],
      errors: errors.concat(
        `Missing required column${missingColumns.length > 1 ? 's' : ''}: ${missingColumns.join(', ')}`,
      ),
    };
  }

  const rows: Array<ParsedSlidingScaleRow> = [];

  parsed.data.forEach((raw, index) => {
    // Header occupies line 1, so the first data row is line 2.
    const rowNumber = index + 2;

    const tourConfiguration = (raw[CSV_COLUMNS.PACKAGE] ?? '').trim();
    const packageConfiguration = (raw[CSV_COLUMNS.PACKAGE_OPTION] ?? '').trim();
    const currency = (raw[CSV_COLUMNS.CURRENCY] ?? '').trim().toUpperCase();
    const season = (raw[CSV_COLUMNS.SEASON] ?? '').trim();

    if (!tourConfiguration || !packageConfiguration) {
      errors.push(`Row ${rowNumber}: ${CSV_COLUMNS.PACKAGE} and ${CSV_COLUMNS.PACKAGE_OPTION} are required`);
      return;
    }

    if (!currency) {
      errors.push(`Row ${rowNumber}: ${CSV_COLUMNS.CURRENCY} is required`);
      return;
    }

    if (!season) {
      errors.push(`Row ${rowNumber}: ${CSV_COLUMNS.SEASON} is required`);
      return;
    }

    const { scale, error } = parseScale(raw, rowNumber);
    if (error) {
      errors.push(error);
      return;
    }

    rows.push({
      rowNumber,
      item: {
        season,
        tourConfiguration,
        packageConfiguration,
        currency,
        // A blank supplement means the supplier charges none.
        singleSupplement: parseAmount(raw[CSV_COLUMNS.SINGLE_SUPPLEMENT]) ?? 0,
        scale,
      },
    });
  });

  return { rows, errors };
}

export function downloadCsv(filename: string, csv: string): void {
  // Excel needs the BOM to read UTF-8 content correctly.
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
